/**
 * world_layers — the translucent things the worlds layer hangs on a body (natural objects, 2026-10-07): a TAIL (a
 * comet's ion tail and dust fan, an evaporating planet's wisp, a moon's jets and plumes), a GLOW (a coma), and a flat
 * DISC in a planet's own plane (a forming giant's circumplanetary disc, the plasma torus a volcanic moon feeds).
 *
 * THE BODY LAYER LAW (atlas/CLAUDE.md "Planets from a distance"). Past 100 km a body is a scaled impostor where one depth
 * code is ~12 km, so nothing here asks the depth buffer where it stands against its own planet:
 *  - a thing ROUND A PLANET (`planetR`) is TWO meshes over one geometry, cut per fragment by the planet's LIMB plane
 *    (P·C = R²): the half on the camera's side is in front of the visible cap on every view ray and stands 'over'; the
 *    other half is behind it and stands 'behind' (space_light_look.seatBodyLayer — the ring's own recipe, which is exact
 *    for ANY point outside the sphere, not only a ring's). A fragment inside the planet's sphere is dropped.
 *  - a thing on its OWN far body (a comet, a moon's plume) stands 'behind': a backdrop on the far plane while its body
 *    is an impostor, its true depth beside it (the nebulae's doctrine — a far translucent thing is a backdrop).
 * Every material is a node material marked as an MRT overlay; nothing here writes depth.
 */
import * as THREE from 'three/webgpu';
import {
  Fn, uniform, float, vec2, vec3, vec4, positionGeometry, positionWorld, cameraPosition, cameraWorldMatrix, modelWorldMatrix, modelWorldMatrixInverse,
  normalize, cross, dot, length, max, min, abs, exp, pow, mix, smoothstep, step, select, clamp, mx_noise_float, time,
} from 'three/tsl';
import { markMrtOverlay } from 'enginex/render/post/mrtOverlay.js';
import { seatBodyLayer, planetFrame } from '../space_light_look.js';
import { OVER_REACH, RING_MESH } from '../space_look.js';
import { RING_ORDER } from '../rings.js';
import { DUST_CURL } from './world_look.js';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type N = any;
type U<T> = { value: T };
const hexV = (hex: number): N => { const c = new THREE.Color(hex); return vec3(c.r, c.g, c.b); };
/** Henyey–Greenstein, normalised so an isotropic grain (g = 0) is 1. */
const hgN = (g: number, cosT: N): N => min(float(1 - g * g).div(pow(max(float(1 + g * g).sub(cosT.mul(2 * g)), float(1e-4)), float(1.5))), float(8));

function overlay(name: string): THREE.MeshBasicNodeMaterial {
  const mat = new THREE.MeshBasicNodeMaterial({ transparent: true, depthWrite: false, side: THREE.DoubleSide });
  mat.blending = THREE.AdditiveBlending;
  mat.forceSinglePass = true; // a sheet of light has no back: one pass, both faces
  mat.fog = false;
  mat.name = name;
  return markMrtOverlay(mat);
}
/** The limb-plane cut for a mesh whose origin is a planet's centre: 1 where this half keeps the fragment, and never inside the sphere. */
function limbKeep(half: 'front' | 'back', R: number): N {
  const f = planetFrame();
  const side = dot(f.rel, f.camRel).sub(R * R);
  const keep = half === 'front' ? side.greaterThan(0) : side.lessThanEqual(0);
  return select(keep, step(R, length(f.rel)), float(0));
}

// ── tails ───────────────────────────────────────────────────────────────────────────────────────────────────────
export type TailProfile = 'ion' | 'dust' | 'gas' | 'jet' | 'umbrella';
export interface TailOpts {
  profile: TailProfile;
  /** Head and far-end colours (sRGB hex). */
  color: number; color2?: number;
  seed: number;
  /** The tail belongs to a planet of this radius at the mesh's origin: two meshes, the limb-plane cut. */
  planetR?: number;
  /** Strip resolution (along × across). */
  segs?: number; across?: number;
  /** Forward-scattering asymmetry of its grains (0 = it glows by itself). */
  g?: number;
  /**
   * The strip stands on a body of this radius at the mesh's origin (a moon's plume): it is seen at the LIMB only — where
   * the sight line meets the body's disc it is dropped (the sector's depth codes are hundreds of metres wide at a
   * moon's distance: a plume against its own disc was cut along a staircase).
   */
  bodyR?: number;
}
export interface TailHandle {
  meshes: THREE.Mesh[];
  /** The axis it streams along and the way it curls back (unit, the mesh's own frame = world orientation); the sun's direction (toward it). */
  axis: THREE.Vector3; lag: THREE.Vector3; sun: THREE.Vector3;
  /** `startM` where it begins along the axis, `lenM` its length, `curl` the share it bends toward `lag`, `w0` / `w1` its half-width at the head and the end, `gain`. */
  set(startM: number, lenM: number, curl: number, w0: number, w1: number, gain: number): void;
  state(): Record<string, number>;
}
/**
 * A tail: a strip along a centreline, turned to face the camera about that line (a cylindrical billboard), or — the
 * DUST profile — a fan lying in the plane of `axis` and `lag` (dust falls behind in the orbit's plane; world_look.
 * dustTailPoint is its CPU mirror). The geometry holds (u across −1..1, s along 0..1); the vertex stage places it.
 */
let tailRefine = 1;
/** PROBE ONLY (the natural-worlds bed's refined-mesh A/B): multiply every tail's mesh density. 1 = as shipped. */
export function setTailRefine(k: number): void { tailRefine = Number.isFinite(k) && k >= 1 ? Math.min(8, Math.round(k)) : 1; }
export function buildTail(o: TailOpts): TailHandle {
  const segs = (o.segs ?? 40) * tailRefine, across = (o.across ?? 8) * tailRefine;
  const pos = new Float32Array((segs + 1) * (across + 1) * 3);
  const idx: number[] = [];
  for (let j = 0; j <= segs; j++) for (let i = 0; i <= across; i++) {
    const k = (j * (across + 1) + i) * 3;
    const t = j / segs;
    pos[k] = (i / across) * 2 - 1; pos[k + 1] = t * t; pos[k + 2] = 0; // finer near the head (and its planet's disc)
  }
  for (let j = 0; j < segs; j++) for (let i = 0; i < across; i++) {
    const a = j * (across + 1) + i, b = a + 1, c = a + across + 1, d = c + 1;
    idx.push(a, c, b, b, c, d);
  }
  const geo = new THREE.BufferGeometry();
  geo.setAttribute('position', new THREE.BufferAttribute(pos, 3));
  geo.setIndex(idx);
  const uAxis = uniform(new THREE.Vector3(0, 0, 1)), uLag = uniform(new THREE.Vector3(1, 0, 0)), uSun = uniform(new THREE.Vector3(0, 0, -1));
  const uShape = uniform(new THREE.Vector4(1, 0, 1, 1)); // len, curl, w0, w1
  const uStart = uniform(0), uGain = uniform(0);
  const dust = o.profile === 'dust';
  const c1 = hexV(o.color), c2 = hexV(o.color2 ?? o.color);
  // the strip's half-width at s: a fan's own spread is its curl (the billboard only gives it body); an umbrella is the
  // envelope of everything thrown from one vent at one speed — a parabola, WIDEST at the ground (w0) and closed at its top
  const halfWAt = (s: N): N => (dust ? mix(uShape.z, uShape.w.mul(0.22), pow(s, float(0.7)))
    : o.profile === 'umbrella' ? uShape.z.mul(pow(max(float(1).sub(s), float(0)), float(0.5)))
      : mix(uShape.z, uShape.w, pow(s, float(0.7))));
  const make = (half: 'front' | 'back' | 'one'): THREE.Mesh => {
    const mat = overlay(`natural-tail-${o.profile}${half === 'one' ? '' : `-${half}`}`);
    mat.positionNode = Fn(() => {
      const u = positionGeometry.x, s = positionGeometry.y;
      const len = uShape.x, w = u.mul(0.5).add(0.5);
      // the centreline: straight down the axis, curling toward the lag as s² (the dust fan spreads the curl across u)
      const curl: N = dust ? uShape.y.mul(mix(float(DUST_CURL.lead), float(DUST_CURL.trail), w)) : uShape.y;
      const along = uStart.add(s.mul(len).mul(float(1).sub(curl.mul(s).mul(0.25))));
      const c = uAxis.mul(along).add(uLag.mul(len.mul(curl).mul(s).mul(s)));
      const T = normalize(uAxis.mul(float(1).sub(curl.mul(s).mul(0.5))).add(uLag.mul(curl.mul(s).mul(2))));
      const camL = modelWorldMatrixInverse.mul(vec4(cameraPosition, 1)).xyz;
      const sideRaw = cross(T, camL.sub(c));
      const side = sideRaw.div(max(length(sideRaw), float(1e-6)));      // seen straight down its length the strip closes (a coma covers it)
      return c.add(side.mul(u.mul(halfWAt(s))));
    })();
    mat.colorNode = Fn(() => {
      const u = positionGeometry.x, s = positionGeometry.y;
      const keep: N = half === 'one' || o.planetR === undefined ? float(1) : limbKeep(half, o.planetR);
      const sd = float(o.seed % 61);
      let acrossI: N, alongI: N, fine: N = float(1);
      if (o.profile === 'ion') {
        // a narrow bright spine in a fainter sheath, combed into rays that drift down the tail
        acrossI = exp(u.mul(2.3).mul(u.mul(2.3)).negate()).mul(0.6).add(exp(u.mul(7).mul(u.mul(7)).negate()).mul(0.55));
        alongI = pow(float(1).sub(s), float(1.5)).mul(smoothstep(0.0, 0.012, s));
        fine = mx_noise_float(vec3(u.mul(6.5), s.mul(9).sub(time.mul(0.02)), sd)).mul(0.3).add(0.72);
      } else if (o.profile === 'dust') {
        // a broad smooth fan, brighter toward its leading edge, with faint striae
        acrossI = pow(max(float(1).sub(u.mul(u)), float(0)), float(1.3)).mul(mix(float(1), float(0.5), u.mul(0.5).add(0.5)));
        alongI = pow(float(1).sub(s), float(0.85)).mul(smoothstep(0.0, 0.02, s));
        fine = mx_noise_float(vec3(u.mul(2.6), s.mul(11), sd)).mul(0.14).add(0.88);
      } else if (o.profile === 'gas') {
        // an escaping envelope: it begins UPSTREAM of its planet (a negative start) and swells to its brightest just
        // behind it — a soft head that holds the planet, never a strip that starts at a line
        acrossI = exp(u.mul(1.9).mul(u.mul(1.9)).negate());
        const head = clamp(uStart.negate().div(uShape.x).mul(2.2), 0.02, 0.6);
        alongI = pow(float(1).sub(s), float(1.35)).mul(smoothstep(0, 1, s.div(head)));
        fine = mx_noise_float(vec3(u.mul(3), s.mul(6).sub(time.mul(0.015)), sd)).mul(0.2).add(0.82);
      } else if (o.profile === 'jet') {
        acrossI = exp(u.mul(2.1).mul(u.mul(2.1)).negate());
        alongI = pow(float(1).sub(s), float(1.1)).mul(exp(s.mul(-2.2)).mul(0.75).add(0.25));
      } else {
        // an umbrella: a thin dome seen through — brightest along its rim (the sight line runs through the most
        // shell there), a fainter fill, a bright column over the vent
        const au = abs(u);
        acrossI = float(0.42).div(pow(max(float(1).sub(au.mul(au)), float(0.06)), float(0.5))).min(1.6).mul(0.5).add(exp(u.mul(5).mul(u.mul(5)).negate()).mul(exp(s.mul(-3.5))).mul(0.9));
        alongI = smoothstep(0.0, 0.05, s).mul(float(1).sub(smoothstep(0.82, 1.0, s)).mul(0.7).add(0.3));
      }
      // forward scattering: grains are brightest seen against their star
      const view = normalize(positionWorld.sub(cameraPosition));
      const phase: N = o.g ? hgN(o.g, dot(view, uSun)) : float(1);
      const edge = float(1).sub(smoothstep(0.86, 1.0, abs(u)));
      // A STRIP IS NOT A VOLUME: seen down its own length a billboard is a sheet across the view, and a camera inside
      // the tail looks through one flat wall of it (the first bed pictures) — so it thins as the view lines up with the
      // axis and where it passes the eye closer than its own width. (The dust fan lies in the orbit's plane: it has no
      // billboard to close, only the near fade.)
      const k = length(modelWorldMatrix.mul(vec4(1, 0, 0, 0)).xyz);
      const nearK = smoothstep(0.35, 1.6, length(positionWorld.sub(cameraPosition)).div(k).div(max(halfWAt(s), float(1e-3))));
      const endOn: N = dust ? float(1) : smoothstep(0.1, 0.45, length(cross(view, uAxis)));
      // its own body: where the strip leaves a planet's sphere it would begin at a LINE across the disc (a sheet cut by
      // a ball) — over the disc it grows out of the surface instead; a moon's plume is not drawn over its moon at all
      let own: N = float(1);
      const bodyR = o.planetR ?? o.bodyR;
      if (bodyR !== undefined) {
        const f = planetFrame();
        const dirT = normalize(f.rel.sub(f.camRel));
        const b = length(cross(f.camRel, dirT));                                  // the sight line's miss distance from the centre
        const over = float(1).sub(smoothstep(bodyR * 0.96, bodyR * 1.03, b));
        own = o.planetR !== undefined ? mix(float(1), smoothstep(bodyR, bodyR * 1.7, length(f.rel)), over) : float(1).sub(over);
      }
      const col = mix(c1, c2, s).mul(uGain).mul(acrossI).mul(alongI).mul(fine).mul(phase).mul(edge).mul(nearK).mul(endOn).mul(own);
      return vec4(col.mul(keep), 1);
    })();
    if (half === 'front') seatBodyLayer(mat, 'over', (o.planetR ?? 1) * OVER_REACH.ring); else seatBodyLayer(mat, 'behind');
    const mesh = new THREE.Mesh(geo, mat);
    mesh.name = mat.name;
    mesh.frustumCulled = false; // the geometry holds (u, s), not places
    mesh.renderOrder = half === 'front' ? RING_ORDER.front : RING_ORDER.back;
    return mesh;
  };
  const meshes = o.planetR !== undefined ? [make('back'), make('front')] : [make('one')];
  const shape = (uShape as unknown as U<THREE.Vector4>).value;
  return {
    meshes,
    axis: (uAxis as unknown as U<THREE.Vector3>).value, lag: (uLag as unknown as U<THREE.Vector3>).value, sun: (uSun as unknown as U<THREE.Vector3>).value,
    set: (startM, lenM, curl, w0, w1, gain) => {
      // A CLAMPED INPUT IS NOT A FINITE ONE: nothing non-finite reaches a uniform
      const f = (v: number): number => (Number.isFinite(v) ? v : 0);
      (uStart as unknown as U<number>).value = f(startM);
      shape.set(Math.max(1e-3, f(lenM)), f(curl), Math.max(0, f(w0)), Math.max(0, f(w1)));
      (uGain as unknown as U<number>).value = Math.max(0, f(gain));
      const on = f(gain) > 1e-4 && f(lenM) > 1;
      for (const m of meshes) m.visible = on;
    },
    state: () => ({ len: shape.x, curl: shape.y, w0: shape.z, w1: shape.w, gain: (uGain as unknown as U<number>).value, start: (uStart as unknown as U<number>).value }),
  };
}

// ── glows ───────────────────────────────────────────────────────────────────────────────────────────────────────
export interface GlowHandle { mesh: THREE.Mesh; sun: THREE.Vector3; /** `backM`: stand the quad this far BEHIND its centre (past the body it glows round). */ set(radiusM: number, gain: number, backM?: number): void; state(): Record<string, number> }
/**
 * A coma: a camera-facing quad whose light falls as 1/ρ from a small bright condensation (the column through a steady
 * outflow), a shade brighter on the star's side. Stands 'behind' (a backdrop while its body is an impostor).
 */
export function buildGlow(o: { color: number; color2?: number; name?: string }): GlowHandle {
  const geo = new THREE.PlaneGeometry(2, 2, 1, 1);
  const uSize = uniform(1), uGain = uniform(0), uSun = uniform(new THREE.Vector3(0, 1, 0)), uBack = uniform(0);
  const mat = overlay(o.name ?? 'natural-glow');
  mat.positionNode = Fn(() => {
    // the camera's own right and up in the mesh's frame: a quad that faces the screen whatever the group's scale
    const right = normalize(modelWorldMatrixInverse.mul(vec4(cameraWorldMatrix.mul(vec4(1, 0, 0, 0)).xyz, 0)).xyz);
    const up = normalize(modelWorldMatrixInverse.mul(vec4(cameraWorldMatrix.mul(vec4(0, 1, 0, 0)).xyz, 0)).xyz);
    // A QUAD THROUGH A BODY'S CENTRE IS CUT BY THE BODY ALONG A STAIRCASE (a depth code is tens of metres wide a few km
    // out): it stands `uBack` behind the centre instead, grown to keep its angular size — the body simply hides its part
    const camL = modelWorldMatrixInverse.mul(vec4(cameraPosition, 1)).xyz;
    const dist = max(length(camL), float(1));
    const grow = uSize.mul(float(1).add(uBack.div(dist)));
    return camL.div(dist).mul(uBack).negate().add(right.mul(positionGeometry.x.mul(grow))).add(up.mul(positionGeometry.y.mul(grow)));
  })();
  const c1 = hexV(o.color), c2 = hexV(o.color2 ?? o.color);
  mat.colorNode = Fn(() => {
    const p = positionGeometry.xy;
    const r = length(p);
    // toward the star the coma is denser (the jets leave the lit side): the sun's direction on the screen
    const sr = dot(cameraWorldMatrix.mul(vec4(1, 0, 0, 0)).xyz, uSun), su = dot(cameraWorldMatrix.mul(vec4(0, 1, 0, 0)).xyz, uSun);
    const lean = dot(p, vec2(sr, su)).mul(0.35).add(1);
    const halo = float(1).div(r.mul(16).add(1)).sub(1 / 17).mul(float(1).sub(smoothstep(0.55, 1.0, r)));
    const core = exp(r.mul(11).mul(r.mul(11)).negate()).mul(1.6);
    // seen from INSIDE, a coma is a thin veil, not a wall of light: the 1/ρ column is what an outside eye sees
    const inside = clamp(length(modelWorldMatrixInverse.mul(vec4(cameraPosition, 1)).xyz).div(uSize.mul(0.9)), 0.07, 1);
    return vec4(mix(c1, c2, smoothstep(0.0, 0.5, r)).mul(max(halo, float(0)).mul(lean).add(core)).mul(uGain).mul(inside), 1);
  })();
  seatBodyLayer(mat, 'behind');
  const mesh = new THREE.Mesh(geo, mat);
  mesh.name = mat.name;
  mesh.frustumCulled = false;
  mesh.renderOrder = RING_ORDER.back;
  return {
    mesh, sun: (uSun as unknown as U<THREE.Vector3>).value,
    set: (radiusM, gain, backM = 0) => {
      const r = Number.isFinite(radiusM) ? Math.max(0, radiusM) : 0, g = Number.isFinite(gain) ? Math.max(0, gain) : 0;
      (uBack as unknown as U<number>).value = Number.isFinite(backM) ? Math.max(0, backM) : 0;
      (uSize as unknown as U<number>).value = Math.max(1e-3, r); (uGain as unknown as U<number>).value = g;
      mesh.visible = g > 1e-4 && r > 1;
    },
    state: () => ({ radius: (uSize as unknown as U<number>).value, gain: (uGain as unknown as U<number>).value }),
  };
}

// ── a disc in a planet's own plane ──────────────────────────────────────────────────────────────────────────────
export interface PlaneDiscOpts {
  kind: 'circumplanetary' | 'torus';
  /** The planet's radius (m) and the disc's reach in planet radii. */
  R: number; rIn: number; rOut: number;
  /** The plane's normal (unit, world orientation). */
  normal: { x: number; y: number; z: number };
  /** The star's light (planet_look.uPlanetSun) and the direction TO the star (the planet's own uniform). */
  sunColor: N; sunDir: { value: THREE.Vector3 };
  color: number; warm?: number;
  /** circumplanetary: its peak optical depth. torus: its ring radius and width (planet radii) ride in rIn / rOut. */
  tau?: number; gain?: number;
}
/**
 * A flat disc round a planet, two meshes over one RingGeometry (the ring's recipe: the limb-plane cut, 'over' in front
 * and 'behind' beyond). The CIRCUMPLANETARY disc is dust: lit by the star through its own slab (brighter where the star
 * stands high over it), shadowed by its planet, warm at its inner edge, and it dims what is behind it. The TORUS only
 * glows: a faint band at one radius whose light grows with the path through it.
 */
export function buildPlaneDisc(o: PlaneDiscOpts): { back: THREE.Mesh; front: THREE.Mesh; geometry: THREE.BufferGeometry } {
  const torus = o.kind === 'torus';
  const mid = (o.rIn + o.rOut) / 2, half = (o.rOut - o.rIn) / 2;
  const gIn = torus ? Math.max(1.02, mid - half * 3) : o.rIn, gOut = torus ? mid + half * 3 : o.rOut;
  const geometry = new THREE.RingGeometry(gIn * o.R, gOut * o.R, RING_MESH.theta, RING_MESH.radial);
  const q = new THREE.Quaternion().setFromUnitVectors(new THREE.Vector3(0, 0, 1), new THREE.Vector3(o.normal.x, o.normal.y, o.normal.z).normalize());
  const nW = new THREE.Vector3(o.normal.x, o.normal.y, o.normal.z).normalize();
  const cD = hexV(o.color), cW = hexV(o.warm ?? o.color);
  const make = (halfName: 'back' | 'front'): THREE.Mesh => {
    const mat = new THREE.MeshBasicNodeMaterial({ transparent: true, depthWrite: false, side: THREE.DoubleSide });
    // emission adds, dust dims, one pass: a premultiplied OVER (the ring's and the nebula's blend)
    mat.blending = THREE.CustomBlending;
    mat.blendSrc = THREE.OneFactor; mat.blendDst = THREE.OneMinusSrcAlphaFactor; mat.blendEquation = THREE.AddEquation;
    mat.blendSrcAlpha = THREE.OneFactor; mat.blendDstAlpha = THREE.OneMinusSrcAlphaFactor; mat.blendEquationAlpha = THREE.AddEquation;
    mat.forceSinglePass = true;
    mat.fog = false;
    mat.name = `natural-${o.kind}-${halfName}`;
    mat.colorNode = Fn(() => {
      const f = planetFrame();
      const P = f.rel, C = f.camRel;
      const rho = length(positionGeometry.xy).div(o.R);
      const n = vec3(nW.x, nW.y, nW.z);
      const V = normalize(C.sub(P));
      const L = normalize(o.sunDir as N);
      const mu = max(abs(dot(V, n)), float(0.06)), mu0 = abs(dot(L, n));
      const keep = limbKeep(halfName, o.R);
      if (torus) {
        const x = rho.sub(mid).div(half);
        const j = exp(x.mul(x).negate()).mul(o.gain ?? 0.05).div(mu).min(1.2);
        return vec4(cD.mul(j).mul(keep), 0);
      }
      // the planet's shadow: a cylinder down the anti-solar line (soft at its edge)
      const t = dot(P, L);
      const perp = length(P.sub(L.mul(t)));
      const lit = mix(smoothstep(o.R * 0.94, o.R * 1.06, perp), float(1), step(0, t));
      const edge = smoothstep(o.rIn, o.rIn + (o.rOut - o.rIn) * 0.08, rho).mul(float(1).sub(smoothstep(o.rIn + (o.rOut - o.rIn) * 0.55, o.rOut, rho)));
      const lanes = mx_noise_float(vec3(rho.mul(22), 0.37, 1.9)).mul(0.25).add(0.8);   // concentric lanes: it is still settling
      const tau = edge.mul(lanes).mul(o.tau ?? 1.5);
      const a = float(1).sub(exp(tau.negate().div(mu)));
      // single scattering off a slab: more of the star's light is caught where it stands high over the plane
      const scat = cD.mul(vec3(o.sunColor)).mul(a).mul(mu0.mul(0.75).add(0.08)).mul(lit).mul(0.3);
      const hot = cW.mul(exp(rho.sub(o.rIn).div((o.rOut - o.rIn) * 0.12).negate())).mul(a).mul(0.9); // the inner edge glows where the planet heats it
      return vec4(scat.add(hot).mul(keep), a.mul(keep));
    })();
    markMrtOverlay(mat);
    if (halfName === 'front') seatBodyLayer(mat, 'over', o.R * OVER_REACH.ring); else seatBodyLayer(mat, 'behind');
    const mesh = new THREE.Mesh(geometry, mat);
    mesh.quaternion.copy(q);
    mesh.name = mat.name;
    mesh.frustumCulled = false;
    mesh.renderOrder = halfName === 'front' ? RING_ORDER.front : RING_ORDER.back;
    return mesh;
  };
  return { back: make('back'), front: make('front'), geometry };
}

/** A unit vector perpendicular to `n` (any — for a plane's frame). */
export function anyPerp(n: THREE.Vector3, out = new THREE.Vector3()): THREE.Vector3 {
  return out.set(0, 1, 0).cross(n).lengthSq() > 1e-6 ? out.normalize() : out.set(1, 0, 0).cross(n).normalize();
}
